import { useState } from "react";
import { downloadBlobAsFile } from "@/lib/document/docExportClient";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import {
  recipeDetailsToExportRows,
  recipesToCsv,
} from "@/lib/manufacturing/recipeCsvExport";
import { exportManufacturingRecipesBundle } from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeDetailResponse } from "@/lib/manufacturing/types";
import { renderRecipesBomPdfToBlob } from "@/components/manufacturing/pdf/generateRecipesBomPdf";

async function loadExportDetails(recipeIds: readonly string[]) {
  const result = await exportManufacturingRecipesBundle(recipeIds);
  if (!result.ok) {
    return { ok: false as const, error: result.error || "Failed to load recipes for export" };
  }
  const details: MfgRecipeDetailResponse[] = result.data.recipes.map((r) => ({
    ok: true as const,
    organization_id: result.data.organization_id,
    recipe: r.recipe,
    lines: r.lines,
    estimated_materials_cost: r.estimated_materials_cost,
    rollup_mode: r.rollup_mode,
  }));
  return { ok: true as const, rows: recipeDetailsToExportRows(details) };
}

function stamp(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Fetch selected recipe details and export CSV or PDF (full BOM lines). */
export function useManufacturingModelsExport() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function exportCsv(recipeIds: readonly string[]) {
    if (recipeIds.length === 0) return;
    setBusy(true);
    setError(null);
    const loaded = await loadExportDetails(recipeIds);
    if (!loaded.ok) {
      setError(loaded.error);
      setBusy(false);
      return;
    }
    downloadCsv(`mfg-recipes-${stamp()}.csv`, recipesToCsv(loaded.rows));
    setBusy(false);
  }

  async function exportPdf(recipeIds: readonly string[]) {
    if (recipeIds.length === 0) return;
    setBusy(true);
    setError(null);
    const loaded = await loadExportDetails(recipeIds);
    if (!loaded.ok) {
      setError(loaded.error);
      setBusy(false);
      return;
    }
    const rendered = await renderRecipesBomPdfToBlob(loaded.rows);
    if (!rendered.ok) {
      setError(rendered.error);
      setBusy(false);
      return;
    }
    downloadBlobAsFile(rendered.blob, `mfg-recipes-${stamp()}.pdf`);
    setBusy(false);
  }

  return { busy, error, exportCsv, exportPdf, clearError: () => setError(null) };
}
